import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { addDays, format, startOfToday } from 'date-fns';

interface CalendarViewProps {
  onDateSelect: (date: Date) => void;
  selectedDate?: Date;
}

export const CalendarView: React.FC<CalendarViewProps> = ({
  onDateSelect,
  selectedDate,
}) => {
  const { user } = useAuth();
  const [view, setView] = React.useState<'month' | 'week'>('month');

  // Fetch services for the current user
  const { data: services } = useQuery({
    queryKey: ['services'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('user_id', user?.id)
        .eq('is_active', true);

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Calculate disabled dates based on availability
  const disabledDates = React.useMemo(() => {
    const today = startOfToday();
    const maxDate = addDays(today, 60); // Allow booking up to 60 days in advance
    
    return {
      before: today,
      after: maxDate,
    };
  }, []);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Calendar</CardTitle>
            <CardDescription>
              Manage your availability and view upcoming bookings
            </CardDescription>
          </div>
          <Select
            value={view}
            onValueChange={(value) => setView(value as 'month' | 'week')}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Month</SelectItem>
              <SelectItem value="week">Week</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col space-y-4">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(date) => date && onDateSelect(date)}
            disabled={disabledDates}
            className="rounded-md border"
          />
          {services?.length === 0 && (
            <div className="text-center py-4">
              <p className="text-muted-foreground">
                No active services found. Create a service to start managing your availability.
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}; 